import { LinkedInstitutionStatus, useCustomerDetails } from '@egw/api'
import { ActionTile, Button, ButtonVariant, Loader, PageHeader } from '@egw/components'
import { DEFAULT_CURRENCY_CODE, WEALTH_ROUTES } from '@egw/constants'
import { useTranslation } from '@egw/i18n'
import { buildBasicCurrency, Currency, formatDateNoTZ } from '@egw/utils'
import { ChevronRightIcon, ExclamationCircleIcon, LinkIcon } from '@heroicons/react/20/solid'
import { type FC, useMemo } from 'react'
import { usePlaidMultiLinkContext } from '../../components'
import { PlaidMultiLinkWrapper } from '../../components/plad-multi-link'

const LinkedAccountsPage: FC = () => {
  const { t } = useTranslation('features-external-accounts')
  const { data, isLoading, isError } = useCustomerDetails()

  const { openModal } = usePlaidMultiLinkContext()

  const linkedExternalInstitutions = useMemo(
    () => data?.customer.externalAccounts.institutions ?? [],
    [data?.customer.externalAccounts.institutions],
  )

  const allAccounts = useMemo(() => {
    return linkedExternalInstitutions.flatMap((institution) => institution.accounts)
  }, [linkedExternalInstitutions])

  const institutionTotals = useMemo(() => {
    return linkedExternalInstitutions.reduce<Record<string, number>>((totals, institution) => {
      const sum = institution.accounts.reduce((acc, account) => acc + (account.externalBalance?.balance ?? 0), 0)
      totals[institution.institutionId] = sum
      return totals
    }, {})
  }, [linkedExternalInstitutions])

  const totalNetValueOfAccounts = useMemo(() => {
    return allAccounts.reduce((acc, account) => acc + (account.externalBalance?.balance ?? 0), 0)
  }, [allAccounts])

  if (isLoading) {
    return (
      <div className="flex justify-center p-md egw-container">
        <Loader />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="border border-borders-dark rounded-lg p-sm egw-container">
        <p className="typo-primary-md-semibold text-text-header mb-2xs">{t('accounts.error_title')}</p>
        <p className="typo-primary-sm-regular text-text-body">{t('accounts.error_message')}</p>
      </div>
    )
  }

  return (
    <>
      <PageHeader
        backLinkUrl={WEALTH_ROUTES.OVERVIEW.path}
        subtitle={t('linked_accounts.subtitle')}
        title={t('linked_accounts.title')}
      />
      <div className="flex flex-col egw-container">
        {/* Net worth summary card - values will be populated by a future API */}
        <div className="overview-card">
          <div className="flex items-end justify-between">
            <div className="flex flex-col">
              <span className="typo-primary-sm-light text-text-meta">{t('linked_accounts.net_value')}</span>
              <span className="typo-primary-2xl-regular text-text-header">
                {buildBasicCurrency({
                  amount: totalNetValueOfAccounts,
                  currencyCode: allAccounts[0]?.externalBalance?.currencyCode ?? DEFAULT_CURRENCY_CODE,
                })}
              </span>
            </div>
          </div>
        </div>
        <Button
          variant={ButtonVariant.Secondary}
          className="self-start w-full mt-lg mb-xl"
          testId="linked-accounts-link-accounts-btn"
          onClick={() => {
            openModal({ modalTitle: t('plaid_multi_link.title') })
          }}
        >
          {t('linked_accounts.link_accounts')}
        </Button>

        {/* Grouped external accounts by institution */}
        {linkedExternalInstitutions.length > 0 ? (
          <div className="flex flex-col gap-lg">
            {linkedExternalInstitutions.map((institution) => (
              <div key={institution.institutionId}>
                <div className="flex items-center justify-between mb-xs">
                  <div className="flex flex-row">
                    {institution.linkedInstitutionStatus === LinkedInstitutionStatus.EXPIRED ||
                    institution.linkedInstitutionStatus === LinkedInstitutionStatus.EXPIRING_SOON ? (
                      <div className="rounded-full w-[26px] h-[26px] bg-red-200 flex items-center justify-center mr-3xs">
                        <ExclamationCircleIcon className="h-4 w-4 text-alert-300 self-center" />
                      </div>
                    ) : null}
                    <h3 className="typo-primary-lg-semibold text-text-header">{institution.institutionName}</h3>
                    <button
                      type="button"
                      onClick={() => {
                        openModal({ institutionId: institution.institutionId, modalTitle: institution.institutionName })
                      }}
                    >
                      <ChevronRightIcon className="w-7 h-7 fill-icons-full flex-shrink-0 self-start hover:cursor-pointer" />
                    </button>
                  </div>
                  <span className="typo-primary-lg-semibold text-text-header">
                    {buildBasicCurrency({
                      amount: institutionTotals[institution.institutionId],
                      currencyCode: institution.accounts[0]?.externalBalance?.currencyCode ?? DEFAULT_CURRENCY_CODE,
                    })}
                  </span>
                </div>
                <div className="flex flex-col gap-2xs">
                  {institution.accounts.map((account) => (
                    <ActionTile
                      key={account.alias}
                      variant="external-account"
                      title={account.name}
                      description={
                        account.externalBalance?.lastUpdated
                          ? t('linked_accounts.last_refreshed', {
                              data: formatDateNoTZ(account.externalBalance.lastUpdated, t('date.long')),
                            })
                          : ''
                      }
                      value={
                        typeof account.externalBalance?.balance === 'number'
                          ? Currency.integerCents.onlyMinus(account.externalBalance.balance)
                          : ''
                      }
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-row gap-2xs">
            <div className="rounded-full p-2xs bg-background-light h-fit flex items-center justify-center mr-3xs">
              <LinkIcon className="w-7 h-7 fill-icons-full flex-shrink-0 self-start" />
            </div>
            <div className="flex flex-col">
              <p className="text-text-header typo-primary-base-semibold">{t('linked_accounts.no_linked_accounts')}</p>
              <p className="text-text-body typo-primary-base-regular">
                {t('linked_accounts.no_linked_accounts_message')}
              </p>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export const WrappedLinkedAccountsPage: FC = () => {
  return (
    <PlaidMultiLinkWrapper>
      <LinkedAccountsPage />
    </PlaidMultiLinkWrapper>
  )
}
